// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    createHandlerChainRunner,
    Handler,
    HasOnError,
    HasOnSuccess,
    markSkipOnCompleteIfOnErrorIsSet,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import { AccountSummaryModel, getAccountSummary } from "@vanmeter/App/Services/AccountSummaryService";

export type LoadAccountSummaryParameter = HasOnSuccess<AccountSummaryModel> &
    HasOnError<string> & {
        numberOfMonthsToLookBack: number;
    };

type Props = {
    apiResult: AccountSummaryModel;
    error?: unknown;
};

type HandlerType = Handler<LoadAccountSummaryParameter, Props>;

export const RequestDataFromApi: HandlerType = async props => {
    try {
        const numberOfMonthsToLookBack = props.parameter.numberOfMonthsToLookBack;
        props.apiResult = await getAccountSummary({ numberOfMonthsToLookBack });
    } catch (error) {
        props.error = error;
    }
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (!props.error) {
        markSkipOnCompleteIfOnSuccessIsSet(props);
        props.parameter.onSuccess?.(props.apiResult);
    }
};

export const ExecuteOnErrorCallback: HandlerType = props => {
    if (props.error) {
        markSkipOnCompleteIfOnErrorIsSet(props);
        const message = props.error instanceof Error ? props.error.message : String(props.error);
        props.parameter.onError?.(message);
    }
};

export const chain = [RequestDataFromApi, ExecuteOnSuccessCallback, ExecuteOnErrorCallback];

const loadAccountSummary = createHandlerChainRunner<LoadAccountSummaryParameter, Props>(chain, "loadAccountSummary");
export default loadAccountSummary;
